import { useState, useMemo, useCallback, useEffect } from 'react';
import { AvailableNewCitationsRowData } from '../components/SelectCitations';
import { useCitation } from '../Providers/CitationProvider';
import { AvailableNewCitation } from '../types';

/**
 * Transforms citation item from API format to table row format
 */
const transformCitationItemToRowData = (
  item: AvailableNewCitation,
  index: number
): AvailableNewCitationsRowData => ({
  id: index + 1,
  citationSite: item.citationSite,
  typeOfSite: '-',
  authority: '-',
  value: '-',
  notes: [],
  cost: item.cost,
  citationSiteType: item.citationSiteType || 'publisher',
  selected: item.selected,
});

/**
 * Calculates total price for selected citations based on citationSite
 */
const calculateSelectedTotalPrice = (tableData: AvailableNewCitationsRowData[], selectedSites: AvailableNewCitation[]): number => {
  return tableData
    .filter((row) => selectedSites.some((site) => site.citationSite === row.citationSite))
    .reduce((total, row) => total + row.cost, 0);
};

export interface UseAvailableNewCitationsReturn {
  // Data
  tableData: AvailableNewCitationsRowData[];
  selected: AvailableNewCitation[];
  totalPrice: number;

  // Sorting
  orderBy: string;
  order: 'asc' | 'desc';

  // Handlers
  handleSelect: (id: number | string) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveAll: () => void;
  handleRequestSort: (property: string) => void;
  handleSelectMultiple: (ids: (number | string)[], select: boolean) => void;
}

/**
 * Custom hook for managing available new citations selection, sorting, and pricing
 */
export const useAvailableNewCitations = (): UseAvailableNewCitationsReturn => {
  const { state: citationState, setSelectedAvailableNewCitations } =
    useCitation();

  const selected = citationState.selectedAvailableNewCitations;
  // Sorting state
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const citations = citationState.campaignDetails?.availableCitations?.citations;
    if (citations) {
      const selectedCitations = citations.filter((citation) => citation.selected);
      setSelectedAvailableNewCitations(selectedCitations);
    }
  }, [citationState.campaignDetails]);

  // Transform API data to table format
  const tableData = useMemo<AvailableNewCitationsRowData[]>(() => {
    if (!citationState.campaignDetails?.availableCitations) {
      return [];
    }
    return citationState.campaignDetails.availableCitations.citations.map(
      (item, index) => transformCitationItemToRowData(item, index)
    );
  }, [citationState.campaignDetails]);

  // Calculate total price for selected citations
  const totalPrice = useMemo(
    () => calculateSelectedTotalPrice(tableData, selected),
    [tableData, selected]
  );

  // Sort handler
  const handleRequestSort = useCallback(
    (property: string) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrderBy(property);
      setOrder(isAsc ? 'desc' : 'asc');
    },
    [orderBy, order]
  );

  // Selection handlers
  const handleSelect = useCallback(
    (id: number | string) => {
      // check if id is already selected ir not
      const isSelected = selected.some((item) => item.citationSite === id);
      if (isSelected) {
        setSelectedAvailableNewCitations(selected.filter((item) => item.citationSite !== id));
      } else {
        // setSelectedAvailableNewCitations([...selected, { citationSite: id, citationSiteType: '', cost: 0 }]);
        const citation = tableData.find((item) => item.citationSite === id);
        if (citation) {
          setSelectedAvailableNewCitations([...selected, citation]);
        }
      }
    },
    [selected, setSelectedAvailableNewCitations, tableData]
  );

  const handleSelectMultiple = useCallback(
    (ids: (number | string)[], select: boolean) => {
      if (select) {
        setSelectedAvailableNewCitations([...selected, ...tableData.filter((item) => ids.includes(item.citationSite))]);
      } else {
        setSelectedAvailableNewCitations(selected.filter((item) => !ids.includes(item.citationSite)));
      }
    },
    [selected, setSelectedAvailableNewCitations, tableData]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // check if checkbox is checked or not
      if (event.target.checked) {
        setSelectedAvailableNewCitations(tableData);
      } else {
        setSelectedAvailableNewCitations([]);
      }
    },
    [tableData, selected, setSelectedAvailableNewCitations]
  );

  const handleRemoveAll = useCallback(() => {
    setSelectedAvailableNewCitations([]);
  }, [setSelectedAvailableNewCitations]);

  return {
    tableData,
    selected,
    totalPrice,
    orderBy,
    order,
    handleSelect,
    handleSelectMultiple,
    handleSelectAll,
    handleRemoveAll,
    handleRequestSort
  };
};
